import * as React from "react";
import { useRequestCategories } from "@/hooks/useRequestCategories";
import { useRequests } from "@/hooks/useRequests";
import { Request, RequestCategory } from "@/types/research.types";
import { filterCategories, filterRequests } from "./request-gallery-context.utils";

type Mode = "list" | "create-category" | "create-request" | "edit-category" | "edit-request";

interface OpenGalleryOptions {
  type?: "discuss" | "research";
  initialData?: { name: string; content: string };
  onSelect?: (content: string, request?: Request) => void;
}

interface RequestGalleryContextType {
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  selectedCategoryId: string | null;
  setSelectedCategoryId: React.Dispatch<React.SetStateAction<string | null>>;
  currentParentId: string | null;
  setCurrentParentId: React.Dispatch<React.SetStateAction<string | null>>;
  categories: RequestCategory[] | undefined;
  isLoadingCategories: boolean;
  mode: Mode;
  setMode: React.Dispatch<React.SetStateAction<Mode>>;
  editingCategory: RequestCategory | null;
  editingRequest: Request | null;
  setEditingRequest: React.Dispatch<React.SetStateAction<Request | null>>;
  handleEditCategory: (category: RequestCategory) => void;
  filteredCategories: RequestCategory[];
  filteredRequests: Request[];
  selectedCategory: RequestCategory | null;
  currentCategory: RequestCategory | null;
  handleCategoryClick: (categoryId: string) => void;
  handleBackToCategories: () => void;
  handleBackToParentCategory: () => void;
  handleBack: () => void;
  activeCategoryId: string | null;
  requests: Request[] | undefined;
  isLoadingRequests: boolean;
  type: "discuss" | "research";
  activeGalleryId: string | null;
  openGallery: (id: string, options?: OpenGalleryOptions) => void;
  closeGallery: () => void;
  initialData: { name: string; content: string } | null;
  onSelect?: (content: string, request?: Request) => void;
  setType: React.Dispatch<React.SetStateAction<"discuss" | "research">>;
}

export const RequestGalleryContext = React.createContext<RequestGalleryContextType | undefined>(
  undefined
);

function useGalleryNavigation(
  setSelectedCategoryId: React.Dispatch<React.SetStateAction<string | null>>,
  setCurrentParentId: React.Dispatch<React.SetStateAction<string | null>>,
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>,
  setMode: React.Dispatch<React.SetStateAction<Mode>>,
  setEditingCategory: React.Dispatch<React.SetStateAction<RequestCategory | null>>,
  setEditingRequest: React.Dispatch<React.SetStateAction<Request | null>>
) {
  const handleCategoryClick = (categoryId: string) => {
    setSelectedCategoryId(categoryId);
    setSearchQuery("");
  };
  const handleBackToCategories = () => {
    setSelectedCategoryId(null);
    setSearchQuery("");
  };
  const handleBackToParentCategory = () => {
    setCurrentParentId(null);
  };
  const handleEditCategory = (category: RequestCategory) => {
    setEditingCategory(category);
    setMode("edit-category");
  };
  const handleBack = () => {
    setMode("list");
    setEditingCategory(null);
    setEditingRequest(null);
  };
  return {
    handleCategoryClick,
    handleBackToCategories,
    handleBackToParentCategory,
    handleEditCategory,
    handleBack,
  };
}

function useGalleryLifecycle(
  setType: React.Dispatch<React.SetStateAction<"discuss" | "research">>,
  setInitialData: React.Dispatch<React.SetStateAction<{ name: string; content: string } | null>>,
  setMode: React.Dispatch<React.SetStateAction<Mode>>,
  setOnSelect: React.Dispatch<
    React.SetStateAction<((content: string, request?: Request) => void) | undefined>
  >,
  setActiveGalleryId: React.Dispatch<React.SetStateAction<string | null>>,
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>
) {
  const openGallery = (id: string, options?: OpenGalleryOptions) => {
    if (options?.type) setType(options.type);
    if (options?.initialData) {
      setInitialData(options.initialData);
      setMode("create-request");
    } else {
      setInitialData(null);
      setMode("list");
    }
    if (options?.onSelect) setOnSelect(() => options.onSelect);
    else setOnSelect(undefined);
    setActiveGalleryId(id);
  };
  const closeGallery = () => {
    setActiveGalleryId(null);
    setTimeout(() => {
      setMode("list");
      setInitialData(null);
      setOnSelect(undefined);
      setSearchQuery("");
    }, 300);
  };
  return { openGallery, closeGallery };
}

function useFilteredData(
  categories: RequestCategory[],
  requests: Request[] | undefined,
  searchQuery: string
) {
  const filteredCategories = React.useMemo(
    () => filterCategories(categories, searchQuery),
    [categories, searchQuery]
  );
  const filteredRequests = React.useMemo(
    () => filterRequests(requests, categories, searchQuery),
    [requests, categories, searchQuery]
  );
  return { filteredCategories, filteredRequests };
}

function useSelectedCategories(
  categories: RequestCategory[],
  selectedCategoryId: string | null,
  currentParentId: string | null
) {
  const selectedCategory = React.useMemo(
    () =>
      selectedCategoryId == null
        ? null
        : (categories.find((cat) => cat._id === selectedCategoryId) ?? null),
    [selectedCategoryId, categories]
  );
  const currentCategory = React.useMemo(
    () =>
      currentParentId == null
        ? null
        : (categories.find((cat) => cat._id === currentParentId) ?? null),
    [currentParentId, categories]
  );
  return { selectedCategory, currentCategory };
}

function useGalleryState() {
  const [searchQuery, setSearchQuery] = React.useState("");
  const [selectedCategoryId, setSelectedCategoryId] = React.useState<string | null>(null);
  const [currentParentId, setCurrentParentId] = React.useState<string | null>(null);
  const [activeGalleryId, setActiveGalleryId] = React.useState<string | null>(null);
  const [type, setType] = React.useState<"discuss" | "research">("discuss");
  const [initialData, setInitialData] = React.useState<{ name: string; content: string } | null>(
    null
  );
  const [onSelect, setOnSelect] = React.useState<
    ((content: string, request?: Request) => void) | undefined
  >(undefined);
  const [mode, setMode] = React.useState<Mode>("list");
  const [editingCategory, setEditingCategory] = React.useState<RequestCategory | null>(null);
  const [editingRequest, setEditingRequest] = React.useState<Request | null>(null);

  return {
    searchQuery,
    setSearchQuery,
    selectedCategoryId,
    setSelectedCategoryId,
    currentParentId,
    setCurrentParentId,
    activeGalleryId,
    setActiveGalleryId,
    type,
    setType,
    initialData,
    setInitialData,
    onSelect,
    setOnSelect,
    mode,
    setMode,
    editingCategory,
    setEditingCategory,
    editingRequest,
    setEditingRequest,
  };
}

function useGalleryData(type: "discuss" | "research") {
  const { data: allCategories, isLoading: isLoadingCategories } = useRequestCategories();
  const categories = React.useMemo(() => {
    if (!allCategories) return [];
    return allCategories.filter((cat) => cat.chat_type === type);
  }, [allCategories, type]);
  const { data: requests, isLoading: isLoadingRequests } = useRequests(type);
  return { categories, isLoadingCategories, requests, isLoadingRequests };
}

export const RequestGalleryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const state = useGalleryState();
  const { categories, isLoadingCategories, requests, isLoadingRequests } = useGalleryData(
    state.type
  );

  const setCurrentParentId = state.setCurrentParentId;
  React.useEffect(() => {
    if (state.searchQuery) setCurrentParentId(null);
  }, [state.searchQuery, setCurrentParentId]);

  const { filteredCategories, filteredRequests } = useFilteredData(
    categories,
    requests,
    state.searchQuery
  );
  const { selectedCategory, currentCategory } = useSelectedCategories(
    categories,
    state.selectedCategoryId,
    state.currentParentId
  );
  const nav = useGalleryNavigation(
    state.setSelectedCategoryId,
    state.setCurrentParentId,
    state.setSearchQuery,
    state.setMode,
    state.setEditingCategory,
    state.setEditingRequest
  );
  const lifecycle = useGalleryLifecycle(
    state.setType,
    state.setInitialData,
    state.setMode,
    state.setOnSelect,
    state.setActiveGalleryId,
    state.setSearchQuery
  );

  const value = {
    searchQuery: state.searchQuery,
    setSearchQuery: state.setSearchQuery,
    selectedCategoryId: state.selectedCategoryId,
    setSelectedCategoryId: state.setSelectedCategoryId,
    currentParentId: state.currentParentId,
    setCurrentParentId: state.setCurrentParentId,
    categories,
    isLoadingCategories,
    mode: state.mode,
    setMode: state.setMode,
    editingCategory: state.editingCategory,
    editingRequest: state.editingRequest,
    setEditingRequest: state.setEditingRequest,
    ...nav,
    filteredCategories,
    filteredRequests,
    selectedCategory,
    currentCategory,
    activeCategoryId: state.selectedCategoryId ?? state.currentParentId,
    requests,
    isLoadingRequests,
    type: state.type,
    activeGalleryId: state.activeGalleryId,
    ...lifecycle,
    initialData: state.initialData,
    onSelect: state.onSelect,
    setType: state.setType,
  };

  return <RequestGalleryContext.Provider value={value}>{children}</RequestGalleryContext.Provider>;
};

export const useRequestGallery = () => {
  const context = React.useContext(RequestGalleryContext);
  if (context === undefined) {
    throw new Error("useRequestGallery must be used within a RequestGalleryProvider");
  }
  return context;
};
